import React, { useState, FC, useEffect } from 'react';
import styles from './SearchForm.module.scss';
import { Badge, Form } from 'antd';
import { SelectOption } from '../../../AnnotationManager/Annotation/annotation.types';
import { AppstoreAddOutlined, ExportOutlined, ClearOutlined, EditOutlined, SyncOutlined, FileExcelOutlined } from '@ant-design/icons';
import SearchItem from './SearchItem';
import SearchText from './SearchText';
import SearchButton from './SearchButton';
import { motion } from 'framer-motion';
import { Permissions } from '../../../../shared/models/permissions.enum';
import { UseHaveAnyPermissions } from '../../../../Auth/UseHaveAnyPermissions';
import { SearchFields } from '../Landing.slice';

type Props = {
  isLoading: boolean;
  sites: SelectOption[];
  inspections: SelectOption[];
  turbines: SelectOption[];
  statuses: SelectOption[];
  severities: SelectOption[];
  findingTypes: SelectOption[];
  assessors: SelectOption[];
  selectedSites: string[];
  selectedInspections: string[];
  selectedTurbines: string[];
  selectedStatuses: string[];
  selectedSeverities: string[];
  selectedFindingTypes: string[];
  selectedFindingIDs: string[];
  selectedAssessors: string[];
  showAdvanced: boolean;
  filtersIndicator: boolean;
  showBulk: boolean;
  onPublish?: () => void | undefined;
  onShowBulk?: () => void | undefined;
  onShowAdvanced?: () => void | undefined;
  searchTurbines: (fields: SearchFields) => void;
  onRefresh: () => void;
  onGetReport?: () => void;
  showFindingIdSearch: boolean;
};

const MotionSearchButton = motion.custom(SearchButton);

export const SearchForm: FC<Props> = ({
  sites,
  inspections,
  turbines,
  statuses,
  severities,
  findingTypes,
  assessors,
  isLoading,
  selectedSites,
  selectedInspections,
  selectedTurbines,
  selectedStatuses,
  selectedSeverities,
  selectedFindingTypes,
  selectedFindingIDs,
  selectedAssessors,
  showAdvanced,
  filtersIndicator,
  showBulk,
  onShowAdvanced,
  searchTurbines,
  onRefresh,
  onPublish,
  onShowBulk,
  onGetReport,
  showFindingIdSearch
}) => {
  const [site, setSite] = useState<string | undefined>(selectedSites.length ? selectedSites[0] : undefined);
  const [inspection, setInspection] = useState<string | undefined>(selectedInspections.length ? selectedInspections[0] : undefined);
  const [turbine, setTurbine] = useState<string | undefined>(selectedTurbines.length ? selectedTurbines[0] : undefined);
  const [status, setStatus] = useState<string | undefined>(selectedStatuses.length ? selectedStatuses[0] : undefined);
  const [findingType, setFindingType] = useState<string | undefined>(selectedFindingTypes.length ? selectedFindingTypes[0] : undefined);
  const [severity, setSeverity] = useState<string | undefined>(selectedSeverities.length ? selectedSeverities[0] : undefined);
  const [findingID, setFindingID] = useState<string | undefined>(selectedFindingIDs.length ? selectedFindingIDs[0] : undefined);
  const [assessor, setAssessor] = useState<string | undefined>(selectedAssessors.length ? selectedAssessors[0] : undefined);
  const [cleared, setCleared] = useState(false);

  const canPublish = UseHaveAnyPermissions([Permissions.AnnotationManager_PublishToPortal]);
  const canAssign = UseHaveAnyPermissions([Permissions.AnnotationManager_AssignTurbine, Permissions.AnnotationManager_UnassignTurbine]);

  const statusSelected = (value: string) => {
    setStatus(value);
  };

  const siteSelected = (value: string) => {
    setSite(value);
    setInspection(undefined);
    setTurbine(undefined);
  };

  const inspectionSelected = (value: string) => {
    setInspection(value);
    setTurbine(undefined);
  };

  const turbineSelected = (value: string) => {
    setTurbine(value);
  };

  const severitySelected = (value: string) => {
    setSeverity(value);
  };

  const findingTypeSelected = (value: string) => {
    setFindingType(value);
  };

  const assessorSelected = (value: string) => {
    setAssessor(value);
  };

  const findingIDSelected = (value: string) => {
    setFindingID(value);
  };

  useEffect(() => {
    searchTurbines({ site, inspection, turbine, status, findingType, severity: severity, layer: undefined, findingID, assessor });
  }, [inspection, searchTurbines, site, status, severity, turbine, cleared, findingID, assessor, findingType]);

  useEffect(() => {
    setSite(selectedSites.length ? selectedSites[0] : undefined);
    setInspection(selectedInspections.length ? selectedInspections[0] : undefined);
    setTurbine(selectedTurbines.length ? selectedTurbines[0] : undefined);
    setStatus(selectedStatuses.length ? selectedStatuses[0] : undefined);
    setSeverity(selectedSeverities.length ? selectedSeverities[0] : undefined);
    setFindingType(selectedFindingTypes.length ? selectedFindingTypes[0] : undefined);
    setAssessor(selectedAssessors.length ? selectedAssessors[0] : undefined);
    setFindingID(selectedFindingIDs.length ? selectedFindingIDs[0] : undefined);
  }, [
    selectedInspections,
    selectedSites,
    selectedStatuses,
    selectedTurbines,
    selectedSeverities,
    selectedFindingIDs,
    selectedAssessors,
    selectedFindingTypes
  ]);

  const onClear = (e: any) => {
    setSite(undefined);
    setInspection(undefined);
    setTurbine(undefined);
    setStatus(undefined);
    setSeverity(undefined);
    setFindingType(undefined);
    setAssessor(undefined);
    setCleared(!cleared);
  };

  return (
    <div className='search-form'>
      <Form className={styles.formContainer} layout='vertical'>
        <div className={styles.itemContainer}>
          <SearchItem label='Site' value={site} values={sites} onChange={siteSelected} />
          <SearchItem label='Inspection ID' value={inspection} values={inspections} onChange={inspectionSelected} />
          <SearchItem label='Turbine Name' value={turbine} values={turbines} onChange={turbineSelected} />
          <SearchItem label='Status' value={status} values={statuses} onChange={statusSelected} maxWidth={'250px'} dropdownMatchSelectWidth={false} />
          <SearchItem
            label={showFindingIdSearch ? 'Severity' : 'Highest Severity'}
            value={severity}
            values={severities}
            onChange={severitySelected}
          />
          {showFindingIdSearch && (
            <>
              <SearchItem label='Finding Type' value={findingType} values={findingTypes} onChange={findingTypeSelected} />
              <SearchItem label='Assessor' value={assessor} values={assessors} onChange={assessorSelected} />
            </>
          )}
          {showFindingIdSearch && <SearchText label='Finding number' onChange={findingIDSelected} />}
        </div>
        <div className={styles.actionsContainer}>
          {showAdvanced && onShowAdvanced && (
            <Badge dot={filtersIndicator}>
              <SearchButton tooltip='Advanced Filter' onClick={onShowAdvanced} disabled={isLoading} title={<AppstoreAddOutlined />} />
            </Badge>
          )}
          {showBulk && onShowBulk && onPublish && (
            <>
              {canAssign && (
                <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3, ease: 'easeIn' }}>
                  <MotionSearchButton tooltip='Bulk Update' onClick={onShowBulk} disabled={isLoading} title={<EditOutlined />} />
                </motion.div>
              )}
              {canPublish && (
                <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3, ease: 'easeIn' }}>
                  <MotionSearchButton tooltip='Publish' onClick={onPublish} disabled={isLoading} title={<ExportOutlined />} />
                </motion.div>
              )}
            </>
          )}
          <SearchButton tooltip='Clear' onClick={onClear} disabled={isLoading} title={<ClearOutlined />} />
          <SearchButton tooltip='Refresh' onClick={onRefresh} disabled={isLoading} title={<SyncOutlined />} />
          {onGetReport && <SearchButton tooltip='Report' onClick={onGetReport} disabled={isLoading} title={<FileExcelOutlined />} />}
        </div>
      </Form>
    </div>
  );
};

export default SearchForm;
